import { useMemo, useState } from 'react';
import {
  InfiniteData,
  UseMutationResult,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  APIError,
  DefaultQueryParams,
  FunnelActivityQueryParams,
  TrialVolunteerAnswersParams,
} from '@data';
import {
  acceptedPatientsKeys,
  enrolledPatientsKeys,
  funnelActivityKeys,
  funnelActivityUsersKeys,
  leadSponsorKeys,
  matchedVolunteersKeys,
  pendingLocationChangesKeys,
  publicTrialLocationsKeys,
  publicTrialsKeys,
  teamMandatesKeys,
  trialActivityKeys,
  trialAtomicCriteriaKeys,
  trialContactsKeys,
  trialCriteriaAnswerCountsKeys,
  trialCriteriaKeys,
  trialLocationsKeys,
  trialMilestonesKeys,
  trialSummaryTranslationKeys,
  trialTitleTranslationKeys,
  trialsKeys,
  volunteerAnswersKeys,
  volunteerContactsKeys,
  volunteerOverviewKeys,
} from '@data/constants';
import {
  CriteriaRequestBody,
  ListLocationView,
  ListTrialCriteriaView,
  ListTrialMatchView,
  ListTrialTranslationView,
  ListTrialView,
  ListTrialVolunteerActivityUsersView,
  ListTrialVolunteerActivityView,
  ListTrialAtomicPredicateView,
  TrialAtomicCriteriaActivationBody,
  TrialAtomicCriteriaView,
  TrialAtomicCriterionSuggestionBody,
  TrialBoosterBody,
  TrialContactBody,
  TrialCriteriaAnswerCountsView,
  TrialCriteriaBody,
  TrialCriteriaView,
  TrialDetailsView,
  TrialLocationBody,
  TrialTranslationBody,
  TrialVolunteerAnswersListView,
  TrialVolunteerContactView,
  UpdateTrialBody,
  TrialVolunteerFeedbackBody,
  TrialManagementApiSearchRequest,
  TrialVolunteersOverviewView,
  TrialManagementApiGetLeadSponsorsRequest,
  TrialManagementApiGetLocationsRequest,
  TrialManagementApiGetPublicTrialsRequest,
  ListTrialPublicView,
  TrialCreateBody,
  CriteriaStatus,
  TrialMatchView,
  TrialReachOutView,
  LocationView,
  TrialView,
  ListPublicLocationView,
  TrialReachOutBodyCommunicationKeyEnum,
  TeamMandatesListView,
  TrialManagementApiGetTeamMandatesRequest,
  TrialManagementApiAssignMandateRequest,
  TrialManagementApiUnassignMandateRequest,
  TrialManagementApiGetMatchesRequest,
  TrialManagementApiAddEffectRequest,
  ListTrialLocationView,
  TrialLocationCountriesView,
  TrialManagementApiRemoveEffectRequest,
  ListTrialContactView,
  TrialManagementApiGetPublicLocationsRequest,
  ListSponsorView,
  SponsorView,
  TrialManagementApiCreateMilestoneRequest,
  MilestoneView,
  TrialManagementApiGetMilestonesRequest,
  ListMilestoneListView,
  TrialManagementApiGetMilestoneTokenRequest,
  TrialManagementApiSetCompanionInviteOnlyRequest,
  MilestoneTokenView,
  TrialManagementApiGetEnrolledRequest,
  ListTrialEnrolledView,
  TrialManagementApiGetAcceptedRequest,
  ListTrialAcceptedView,
  ListPendingLocationChangeView,
  TrialManagementApiGetPendingLocationChangesRequest,
  PendingLocationChangeStatus,
  AdvanceMilestoneView,
  OverallTrialStatus,
} from '@data/sdk/match';
import { useAppUserStore } from '@data/storage';
import { MatchingApi } from '@data/utils/axios-instance';
import { useDebounce } from '../../../app/utils/hooks/useDebounce.hook';

export const useTrialsList = (params: TrialManagementApiSearchRequest['getTrialsBody']) => {
  const { user } = useAppUserStore.getState();
  return useQuery<ListTrialView, APIError>({
    queryKey: trialsKeys.list(user?.teamId, params),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.search({ getTrialsBody: params });
      return response.data;
    },
  });
};

const TRIALS_PICKER_PAGE_SIZE = 15;

/**
 * Search-as-you-type trial picker: one page per scroll, labelled `official title (NCT)`.
 * `searchString` is owned by the caller (debounced there) so the input can live in the URL.
 */
export const useTrialsInfiniteList = (
  searchString?: string,
  overallStatuses?: OverallTrialStatus[],
) => {
  const { user } = useAppUserStore.getState();

  const filters = {
    limit: TRIALS_PICKER_PAGE_SIZE,
    searchString: searchString || undefined,
    overallStatuses,
  };

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useInfiniteQuery<
    ListTrialView,
    APIError
  >({
    queryKey: trialsKeys.infiniteList(user?.teamId, filters),
    initialPageParam: { ...filters, offset: 0 },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.trialManagement.search({
        getTrialsBody: pageParam as TrialManagementApiSearchRequest['getTrialsBody'],
      });
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((total, page) => total + page.items.length, 0);
      return nextOffset < lastPage.count ? { ...filters, offset: nextOffset } : undefined;
    },
    placeholderData: keepPreviousData,
  });

  const trials = useMemo(() => data?.pages.flatMap((page) => page.items) ?? [], [data]);

  const trialsOptions = useMemo(
    () =>
      trials.map((trial) => ({
        label: `${trial.trialId} - ${trial.officialTitle}`,
        value: trial.id,
      })),
    [trials],
  );

  return {
    fetchNextPage,
    hasNextPage,
    isLoading: isLoading || isFetching,
    trials,
    trialsOptions,
  };
};

export const usePublicTrialsList = (params: TrialManagementApiGetPublicTrialsRequest) =>
  useQuery<ListTrialPublicView, Error>({
    queryKey: publicTrialsKeys.list(params),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getPublicTrials(params);
      return response.data;
    },
  });

// TODO: move to queries
export const trialDetailQuery = (trialId: string) => ({
  queryKey: trialsKeys.detail(trialId),
  queryFn: async () => {
    const response = await MatchingApi.trialManagement.getDetails({ id: trialId });

    return response.data;
  },
});

export const useTrial = (trialId: string, initialData?: TrialDetailsView, enabled = true) => {
  return useQuery<TrialDetailsView, Error>({
    queryKey: trialsKeys.detail(trialId),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getDetails({ id: trialId });
      return response.data;
    },
    staleTime: 1000 * 60 * 60,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    initialData,
    enabled: !!trialId && enabled,
  });
};

export const usePublicTrialLocationsList = (trialId: string, params: DefaultQueryParams) =>
  useQuery<ListPublicLocationView, Error>({
    queryKey: publicTrialLocationsKeys.list(trialId, { ...params }),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getPublicLocations({
        id: trialId,
        ...params,
      });
      return response.data;
    },
  });

export const usePublicTrialLocationsInfiniteList = (trialId: string) => {
  const [query, setQuery] = useState<DefaultQueryParams>({
    limit: 15,
    offset: 0,
    searchString: undefined,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching, refetch } = useInfiniteQuery<
    ListPublicLocationView,
    APIError
  >({
    queryKey: trialId
      ? publicTrialLocationsKeys.list(trialId, { ...query, searchString: debouncedSearch })
      : [],
    initialPageParam: {
      ...query,
      searchString: debouncedSearch,
      id: trialId,
    },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.trialManagement.getPublicLocations(
        pageParam as TrialManagementApiGetPublicLocationsRequest,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            searchString: debouncedSearch || undefined,
            id: trialId,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
    enabled: !!trialId,
  });

  const locations = data?.pages.flatMap((page) => page.items) || [];

  const locationOptions = useMemo(
    () =>
      locations.map((location) => ({
        ...location,
        label: `${location.city} (${location.country}), ${location.facility}`,
        value: location.id,
      })),
    [locations],
  );

  const handleSearchChange = (v?: string) => {
    setQuery({ ...query, searchString: v || undefined, offset: 0 });
  };

  return {
    locations,
    locationOptions,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    handleSearchChange,
    isInitialLoading: isLoading,
    searchString: query.searchString,
    refetch,
  };
};

export const useTrialCriteriaList = (trialId: string, params: DefaultQueryParams) =>
  useQuery<ListTrialCriteriaView, Error>({
    queryKey: trialCriteriaKeys.list(trialId, params),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getCriteria({ id: trialId, ...params });
      return response.data;
    },
    enabled: !!trialId,
  });

export const useTrialAtomicCriteria = (trialId: string, enabled = true) =>
  useQuery<TrialAtomicCriteriaView, Error>({
    queryKey: trialAtomicCriteriaKeys.detail(trialId),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getAtomicCriteria({ id: trialId });
      return response.data;
    },
    enabled: !!trialId && enabled,
  });

/**
 * The engine applies a switch when it next polls for matching input changes (every 10 s), so the
 * answer counts of a trial poll for a minute after a switch instead of being read once.
 */
const ANSWER_COUNTS_POLL_MS = 5000;
const ANSWER_COUNTS_POLL_WINDOW_MS = 60_000;
const lastSwitchAt = new Map<string, number>();

const isWaitingForEngine = (trialId: string): boolean =>
  Date.now() - (lastSwitchAt.get(trialId) ?? 0) < ANSWER_COUNTS_POLL_WINDOW_MS;

export const useTrialCriteriaAnswerCounts = (trialId: string, enabled = true) =>
  useQuery<TrialCriteriaAnswerCountsView, Error>({
    queryKey: trialCriteriaAnswerCountsKeys.detail(trialId),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getCriteriaAnswerCounts({ id: trialId });
      return response.data;
    },
    enabled: !!trialId && enabled,
    refetchInterval: () => (isWaitingForEngine(trialId) ? ANSWER_COUNTS_POLL_MS : false),
  });

/**
 * Switches atomic criteria off or on. The rows in the cache take the returned state. The answer
 * counts poll until the engine has rebuilt the trial (`isWaitingForEngine`).
 */
export const useSetAtomicCriteriaActivation = (
  trialId: string,
): UseMutationResult<ListTrialAtomicPredicateView, APIError, TrialAtomicCriteriaActivationBody> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body) => {
      const response = await MatchingApi.trialManagement.setAtomicCriteriaActivation({
        id: trialId,
        trialAtomicCriteriaActivationBody: body,
      });
      return response.data;
    },
    onSuccess: ({ items }) => {
      const switched = new Map(items.map((predicate) => [predicate.id, predicate]));
      queryClient.setQueryData<TrialAtomicCriteriaView>(
        trialAtomicCriteriaKeys.detail(trialId),
        (data) =>
          data && {
            ...data,
            criteria: data.criteria.map((criterion) => ({
              ...criterion,
              predicates: criterion.predicates.map(
                (predicate) => switched.get(predicate.id) ?? predicate,
              ),
            })),
          },
      );
      lastSwitchAt.set(trialId, Date.now());
      queryClient.invalidateQueries({ queryKey: trialCriteriaAnswerCountsKeys.detail(trialId) });
      queryClient.invalidateQueries({ queryKey: trialActivityKeys.list(trialId) });
    },
  });
};

/** Sends Curewiki a suggestion to change one atomic criterion. Support gets it in the daily email. */
export const useSuggestAtomicCriterionChange = (
  trialId: string,
): UseMutationResult<
  void,
  APIError,
  { predicateId: string; values: TrialAtomicCriterionSuggestionBody }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ predicateId, values }) => {
      const response = await MatchingApi.trialManagement.suggestAtomicCriterionChange({
        id: trialId,
        predicateId,
        trialAtomicCriterionSuggestionBody: values,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: trialActivityKeys.list(trialId) });
    },
  });
};

export const useActivateAllTrialCriteria = (): UseMutationResult<
  unknown,
  APIError,
  { params: DefaultQueryParams; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ trialId }) => {
      const response = await MatchingApi.trialManagement.validateAll({ id: trialId });
      return response.data;
    },
    onMutate: async ({ trialId, params }) => {
      await queryClient.cancelQueries({ queryKey: trialCriteriaKeys.all() });

      const previousCriteriaList = queryClient.getQueryData<InfiniteData<ListTrialCriteriaView>>(
        trialCriteriaKeys.list(trialId, params),
      );

      if (previousCriteriaList) {
        const newPages = previousCriteriaList.pages.map((page: { items: TrialCriteriaView[] }) => ({
          ...page,
          items: page.items.map((criteria) => ({
            ...criteria,
            status: CriteriaStatus.Validated,
          })),
        }));

        queryClient.setQueryData(trialCriteriaKeys.list(trialId, params), {
          ...previousCriteriaList,
          pages: newPages,
        });
      }

      return { previousCriteriaList };
    },
    onSuccess: (_, { trialId }) =>
      queryClient.invalidateQueries({ queryKey: trialsKeys.detail(trialId) }),

    onError: (_, { trialId, params }, context) =>
      queryClient.setQueryData(
        trialCriteriaKeys.list(trialId, params),
        context?.previousCriteriaList,
      ),
  });
};

export const useAddTrialComment = (): UseMutationResult<
  unknown,
  APIError<{ comment: string }>,
  { comment: string; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, comment }) => {
      const response = await MatchingApi.trialManagement.createComment({
        id: trialId,
        trialCommentBody: { comment },
      });
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: trialActivityKeys.list(trialId) });
    },
  });
};

export const useRequestCriteria = (): UseMutationResult<
  unknown,
  APIError<CriteriaRequestBody>,
  { trialId: string; values: CriteriaRequestBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, values }) => {
      const response = await MatchingApi.trialManagement.requestCriteria({
        id: trialId,
        criteriaRequestBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: trialActivityKeys.list(trialId) });
    },
  });
};

export const useUpdateTrial = (): UseMutationResult<
  unknown,
  APIError,
  { trialId: string; values: UpdateTrialBody },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ trialId, values }) => {
      const response = await MatchingApi.trialManagement.updateTrial({
        id: trialId,
        updateTrialBody: values,
      });
      return response.data;
    },
    onMutate: async ({ trialId, values }) => {
      await queryClient.cancelQueries({ queryKey: trialsKeys.all() });

      const previousTrial = queryClient.getQueryData<TrialDetailsView>(trialsKeys.detail(trialId));

      if (previousTrial && values.managedBy) {
        const newTrial = { ...previousTrial, managedBy: values.managedBy };

        queryClient.setQueryData(trialsKeys.detail(trialId), newTrial);
      }

      return { previousTrial };
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: trialsKeys.all() });
      queryClient.invalidateQueries({ queryKey: trialActivityKeys.list(trialId) });
      queryClient.invalidateQueries({ queryKey: trialCriteriaKeys.all() });
    },

    onError: (_, { trialId }, context) =>
      queryClient.setQueryData(trialsKeys.detail(trialId), context?.previousTrial),
  });
};

export const useTrialContactsList = (trialId: string, params: DefaultQueryParams) =>
  useQuery<ListTrialContactView, Error>({
    queryKey: trialContactsKeys.list(trialId),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getContacts({ id: trialId, ...params });
      return response.data;
    },
    enabled: !!trialId,
    placeholderData: keepPreviousData,
  });

export const useAddTrialContact = (): UseMutationResult<
  unknown,
  APIError<TrialContactBody>,
  { trialId: string; values: TrialContactBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, values }) => {
      const response = await MatchingApi.trialManagement.createContact({
        id: trialId,
        trialContactBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: trialContactsKeys.list(trialId) });
      queryClient.invalidateQueries({ queryKey: trialsKeys.detail(trialId) });
    },
  });
};

export const useTrialLocation = (locationId: string) =>
  useQuery<LocationView, Error>({
    queryKey: trialLocationsKeys.detail(locationId),
    queryFn: async () => {
      const response = await MatchingApi.location.getLocation({ id: locationId });
      return response.data;
    },
    enabled: !!locationId,
  });

export const useTrialLocationsList = (
  trialId: string,
  params: Omit<TrialManagementApiGetLocationsRequest, 'id'>,
  { keepPreviousPage = false }: { keepPreviousPage?: boolean } = {},
) =>
  useQuery<ListTrialLocationView, APIError>({
    queryKey: trialLocationsKeys.list({ id: trialId, ...params }),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getLocations({ id: trialId, ...params });
      return response.data;
    },
    enabled: !!trialId,
    placeholderData: keepPreviousPage ? keepPreviousData : undefined,
  });

export const useTrialLocationCountries = (trialId: string) =>
  useQuery<TrialLocationCountriesView, APIError>({
    queryKey: trialLocationsKeys.countries(trialId),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getLocationCountries({ id: trialId });
      return response.data;
    },
    enabled: !!trialId,
  });

export const useTrialLocationsListInfinite = (trialId: string) => {
  const [query, setQuery] = useState<TrialManagementApiGetLocationsRequest>({
    limit: 20,
    offset: 0,
    searchString: undefined,
    id: trialId,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchedAfterMount,
  } = useInfiniteQuery<ListLocationView, APIError>({
    queryKey: trialLocationsKeys.list({ id: trialId, searchString: debouncedSearch }),
    initialPageParam: {
      ...query,
      searchString: debouncedSearch,
      id: trialId,
    },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.trialManagement.getLocations(
        pageParam as TrialManagementApiGetLocationsRequest,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            id: trialId,
            searchString: debouncedSearch || undefined,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
    enabled: !!trialId,
  });

  const trialLocations = data?.pages.flatMap((page) => page.items) || [];

  const trialLocationOptions = useMemo(
    () =>
      trialLocations.map((location) => ({
        label: `${location.city} (${location.country}) ${
          location.facility ? `, ${location.facility}` : ''
        }`,
        value: location.id,
        permission: `contacts:${location.id}:read`,
      })),
    [trialLocations],
  );

  const trialLocationsCount = data?.pages[0].count || 0;

  const handleSearchChange = (v?: string) => {
    setQuery({ ...query, searchString: v || undefined, offset: 0 });
  };

  return {
    handleSearchChange,
    trialLocationOptions,
    trialLocationsCount,
    trialLocations,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    isFetchedAfterMount,
  };
};

export const useAddTrialLocation = (): UseMutationResult<
  LocationView,
  APIError<TrialLocationBody>,
  { trialId: string; values: TrialLocationBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, values }) => {
      const response = await MatchingApi.trialManagement.createLocation({
        id: trialId,
        trialLocationBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: trialsKeys.detail(trialId) });
      queryClient.invalidateQueries({ queryKey: trialLocationsKeys.all() });
    },
  });
};

export const useAddCriteria = (): UseMutationResult<
  unknown,
  APIError<TrialCriteriaBody>,
  { trialId: string; values: TrialCriteriaBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, values }) => {
      const response = await MatchingApi.trialManagement.createCriteria({
        id: trialId,
        trialCriteriaBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: trialCriteriaKeys.all() });
      queryClient.invalidateQueries({ queryKey: trialsKeys.detail(trialId) });
    },
  });
};

export const useMarkTrialAsFavorite = (): UseMutationResult<
  unknown,
  Error,
  { params?: TrialManagementApiSearchRequest['getTrialsBody']; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  const { user } = useAppUserStore.getState();

  return useMutation({
    mutationFn: async ({ trialId }) => {
      const response = await MatchingApi.trialManagement.addFavoriteTrial({ id: trialId });
      return response.data;
    },
    onMutate: async ({ trialId, params }) => {
      await queryClient.cancelQueries({ queryKey: trialsKeys.all() });

      const previousTrial = queryClient.getQueryData<TrialDetailsView>(trialsKeys.detail(trialId));

      if (previousTrial) {
        const newTrial = { ...previousTrial, isFavorite: true };

        queryClient.setQueryData(trialsKeys.detail(trialId), newTrial);
      }

      const previousTrialsList = queryClient.getQueryData<ListTrialView>(
        trialsKeys.list(user?.teamId, params),
      );

      if (previousTrialsList) {
        const newTrialsList = previousTrialsList.items.map((trial) =>
          trial.id === trialId ? { ...trial, isFavorite: true } : trial,
        );

        queryClient.setQueryData(trialsKeys.list(user?.teamId, params), {
          ...previousTrialsList,
          items: newTrialsList,
        });

        queryClient.getQueryData<ListTrialView>(trialsKeys.list(user?.teamId, params));
      }

      return { previousTrial, previousTrialsList };
    },

    onError: (_, { trialId, params }, context) => {
      queryClient.setQueryData(trialsKeys.detail(trialId), context?.previousTrial);
      queryClient.setQueryData(trialsKeys.list(user?.teamId, params), context?.previousTrialsList);
    },
  });
};

export const useUnMarkTrialAsFavorite = (): UseMutationResult<
  unknown,
  any,
  { params?: TrialManagementApiSearchRequest['getTrialsBody']; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  const { user } = useAppUserStore.getState();
  return useMutation({
    mutationFn: async ({ trialId }) => {
      const response = await MatchingApi.trialManagement.deleteFavoriteTrial({ id: trialId });
      return response.data;
    },
    onMutate: async ({ trialId, params }) => {
      await queryClient.cancelQueries({ queryKey: trialsKeys.detail(trialId) });

      const previousTrial = queryClient.getQueryData<TrialDetailsView>(trialsKeys.detail(trialId));

      if (previousTrial) {
        const newTrial = { ...previousTrial, isFavorite: false };

        queryClient.setQueryData(trialsKeys.detail(trialId), newTrial);
      }

      const previousTrialsList = queryClient.getQueryData<ListTrialView>(
        trialsKeys.list(user?.teamId, params),
      );

      if (previousTrialsList) {
        const newTrialsList = previousTrialsList.items.map((trial) =>
          trial.id === trialId ? { ...trial, isFavorite: false } : trial,
        );

        queryClient.setQueryData(trialsKeys.list(user?.teamId, params), {
          ...previousTrialsList,
          items: newTrialsList,
        });

        queryClient.getQueryData<ListTrialView>(trialsKeys.list(user?.teamId, params));
      }

      return { previousTrial, previousTrialsList };
    },

    onError: (_, { trialId, params }, context) => {
      queryClient.setQueryData(trialsKeys.detail(trialId), context?.previousTrial);
      queryClient.setQueryData(trialsKeys.list(user?.teamId, params), context?.previousTrialsList);
    },
  });
};

export const useDeleteTrial = (): UseMutationResult<unknown, APIError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (trialId) => {
      const response = await MatchingApi.trialManagement.deleteTrial({ id: trialId });
      return response.data;
    },
    onSuccess: (_, trialId) => {
      queryClient.invalidateQueries({ queryKey: trialsKeys.detail(trialId) });
    },
  });
};

export const useRestoreTrial = (): UseMutationResult<unknown, APIError, string, unknown> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (trialId) => {
      const response = await MatchingApi.trialManagement.restoreTrial({ id: trialId });
      return response.data;
    },
    onSuccess: (_, trialId) => {
      queryClient.invalidateQueries({ queryKey: trialsKeys.detail(trialId) });
    },
  });
};

export const useTrialTitleTranslations = (trialId: string) =>
  useQuery<ListTrialTranslationView, Error>({
    queryKey: trialTitleTranslationKeys.list(trialId),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getTitleTranslations({ id: trialId });
      return response.data;
    },
    enabled: !!trialId,
  });

export const useCreateTrialTitleTranslations = (): UseMutationResult<
  ListTrialTranslationView,
  APIError,
  { id: string; values: TrialTranslationBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.trialManagement.createTitleTranslations({
        id,
        trialTranslationBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: trialTitleTranslationKeys.list(id) });
    },
  });
};

export const useTrialSummaryTranslations = (trialId: string) =>
  useQuery<ListTrialTranslationView, Error>({
    queryKey: trialSummaryTranslationKeys.list(trialId),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getSummaryTranslations({ id: trialId });
      return response.data;
    },
    enabled: !!trialId,
  });

export const useCreateTrialSummaryTranslations = (): UseMutationResult<
  ListTrialTranslationView,
  APIError,
  { id: string; values: TrialTranslationBody },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }) => {
      const response = await MatchingApi.trialManagement.createSummaryTranslations({
        id,
        trialTranslationBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: trialSummaryTranslationKeys.list(id) });
    },
  });
};

// funnel page
export const useVolunteersOverview = (trialId: string, locationId?: string, cohortId?: string) =>
  useQuery<TrialVolunteersOverviewView, Error>({
    queryKey: volunteerOverviewKeys.listByCountry(trialId, locationId, cohortId),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getVolunteersOverview({
        id: trialId,
        ...(locationId && { locationId }),
        ...(cohortId && { cohortId }),
      });
      return response.data;
    },
    enabled: !!trialId,
    refetchOnReconnect: true,
  });

export const useVolunteerAnswersMutate = (): UseMutationResult<
  TrialVolunteerAnswersListView,
  APIError,
  { params: TrialVolunteerAnswersParams; trialId: string },
  unknown
> => {
  return useMutation({
    mutationFn: async ({ trialId, params }) => {
      const response = await MatchingApi.trialManagement.getVolunteerAnswers({
        id: trialId,
        ...params,
      });
      return response.data;
    },
  });
};

export const useMatchedVolunteers = (
  trialId: string,
  params: Omit<TrialManagementApiGetMatchesRequest, 'id'>,
  locationId?: string,
) => {
  return useQuery<ListTrialMatchView, Error>({
    queryKey: matchedVolunteersKeys.list({ id: trialId, ...params, locationId }),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getMatches({
        id: trialId,
        ...params,
        locationId: locationId || undefined,
      });
      return response.data;
    },
    enabled: !!trialId,
  });
};

export const useEnrolledPatients = (params: TrialManagementApiGetEnrolledRequest) => {
  return useQuery<ListTrialEnrolledView, Error>({
    queryKey: enrolledPatientsKeys.list(params),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getEnrolled(params);
      return response.data;
    },
    enabled: !!params.id,
  });
};

// Dev/QA only: fast-forward an enrolled patient to their next milestone (backend refuses in production).
export const useAdvanceMilestone = (): UseMutationResult<
  AdvanceMilestoneView,
  APIError,
  { trialId: string; userId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, userId }) => {
      const response = await MatchingApi.trialManagement.advanceMilestone({ id: trialId, userId });
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: enrolledPatientsKeys.allForTrial(trialId) });
    },
  });
};

export const useAcceptedPatients = (params: TrialManagementApiGetAcceptedRequest) => {
  return useQuery<ListTrialAcceptedView, Error>({
    queryKey: acceptedPatientsKeys.list(params),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getAccepted(params);
      return response.data;
    },
    enabled: !!params.id,
  });
};

export const useVolunteerContacts = (trialId: string, volunteerId: string) =>
  useQuery<TrialVolunteerContactView, Error>({
    queryKey: volunteerContactsKeys.list(volunteerId),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getVolunteerContact({
        id: trialId,
        userId: volunteerId,
      });
      return response.data;
    },
    enabled: !!trialId && !!volunteerId,
  });

export const useVolunteerContactsMutate = (): UseMutationResult<
  TrialVolunteerContactView,
  APIError,
  { trialId: string; volunteerId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, volunteerId }) => {
      const response = await MatchingApi.trialManagement.getVolunteerContact({
        id: trialId,
        userId: volunteerId,
      });
      return response.data;
    },
    onSuccess: (data, { volunteerId }) => {
      queryClient.setQueryData(volunteerContactsKeys.list(volunteerId), data);
    },
  });
};

export const useUpdateVolunteerStatusInTrial = (): UseMutationResult<
  unknown,
  APIError,
  { trialId: string; values: TrialVolunteerFeedbackBody; volunteerId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, volunteerId, values }) => {
      const response = await MatchingApi.trialManagement.respondToVolunteer({
        id: trialId,
        userId: volunteerId,
        trialVolunteerFeedbackBody: values,
      });
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: volunteerAnswersKeys.all() });
      queryClient.invalidateQueries({ queryKey: volunteerOverviewKeys.list(trialId) });
    },
  });
};

export const useFunnelActivity = (trialId: string, params: FunnelActivityQueryParams) =>
  useQuery<ListTrialVolunteerActivityView, Error>({
    queryKey: funnelActivityKeys.list(params),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getVolunteerActivity({
        id: trialId,
        ...params,
      });
      return response.data;
    },
    enabled: !!trialId,
  });

export const useFunnelActivityUsers = (trialId: string, params: DefaultQueryParams) =>
  useQuery<ListTrialVolunteerActivityUsersView, Error>({
    queryKey: funnelActivityUsersKeys.list(params),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getVolunteerActivityUsers({
        id: trialId,
        ...params,
      });
      return response.data;
    },
    enabled: !!trialId,
  });

export const useRequestBooster = (): UseMutationResult<
  unknown,
  APIError,
  { trialId: string; values: TrialBoosterBody },
  unknown
> => {
  return useMutation({
    mutationFn: async ({ trialId, values }) => {
      const response = await MatchingApi.trialManagement.requestBooster({
        id: trialId,
        trialBoosterBody: values,
      });
      return response.data;
    },
  });
};

export const useAddSponsor = (): UseMutationResult<
  void,
  APIError,
  { sponsorId: string; trialId: string },
  unknown
> =>
  useMutation({
    mutationFn: async ({ trialId, sponsorId }) => {
      const response = await MatchingApi.trialManagement.addSponsor({
        id: trialId,
        trialAddSponsorBody: {
          sponsorId,
        },
      });
      return response.data;
    },
  });

export const useDeleteSponsor = (): UseMutationResult<
  void,
  APIError,
  { sponsorId: string; trialId: string },
  unknown
> => {
  return useMutation({
    mutationFn: async ({ trialId, sponsorId }) => {
      const response = await MatchingApi.trialManagement.deleteSponsor({
        id: trialId,
        trialDeleteSponsorBody: {
          sponsorId,
        },
      });
      return response.data;
    },
  });
};

export type LeadSponsorSearchableInitialOption = { label: string; value: string };

export const useLeadSponsorsInfiniteList = (
  valuePropName?: keyof SponsorView,
  initialSponsorOption?: LeadSponsorSearchableInitialOption,
) => {
  const valueProp = valuePropName ?? 'name';
  const [query, setQuery] = useState<TrialManagementApiGetLeadSponsorsRequest>({
    limit: 15,
    offset: 0,
    searchString: undefined,
  });

  const debouncedSearch = useDebounce(query.searchString);

  const { data, fetchNextPage, hasNextPage, isLoading, isFetching } = useInfiniteQuery<
    ListSponsorView,
    APIError
  >({
    queryKey: leadSponsorKeys.list({ ...query, searchString: debouncedSearch }),
    initialPageParam: {
      ...query,
      searchString: debouncedSearch,
    },
    queryFn: async ({ pageParam }) => {
      const response = await MatchingApi.trialManagement.getLeadSponsors(
        pageParam as TrialManagementApiGetLeadSponsorsRequest,
      );
      return response.data;
    },
    getNextPageParam: (lastPage, allPages) => {
      const nextOffset = allPages.reduce((prev, next) => prev + next.items.length, 0);

      return nextOffset < lastPage.count
        ? {
            limit: query.limit,
            offset: nextOffset,
            searchString: debouncedSearch || undefined,
          }
        : undefined;
    },
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    gcTime: 1000 * 60 * 60,
    staleTime: 1000 * 60 * 60,
  });

  const leadSponsors = data?.pages.flatMap((page) => page.items) || [];

  const leadSponsorsOptions = useMemo(() => {
    const options = leadSponsors.map((leadSponsor) => ({
      label: leadSponsor.name,
      value: leadSponsor[valueProp],
    }));
    return initialSponsorOption && !options.some((o) => o.value === initialSponsorOption.value)
      ? [initialSponsorOption, ...options]
      : options;
  }, [leadSponsors, initialSponsorOption]);

  const handleSearchChange = (v?: string) => {
    setQuery({ ...query, searchString: v || undefined, offset: 0 });
  };

  return {
    leadSponsors,
    leadSponsorsOptions,
    fetchNextPage,
    hasNextPage,
    isLoading: isLoading || isFetching,
    isFirstLoading: isLoading,
    handleSearchChange,
    searchString: query.searchString,
  };
};

export const useCreateTrial = (): UseMutationResult<
  TrialView,
  APIError,
  { values: TrialCreateBody },
  unknown
> => {
  return useMutation({
    mutationFn: async ({ values }) => {
      const response = await MatchingApi.trialManagement.createTrial({ trialCreateBody: values });
      return response.data;
    },
  });
};

export const useUpdateVolunteerContacted = (): UseMutationResult<
  unknown,
  APIError,
  {
    contactedAt: string;
    locationId?: string;
    params?: Omit<TrialManagementApiGetMatchesRequest, 'id'>;
    trialId: string;
    userId: string;
  },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ contactedAt, trialId, userId }) => {
      const response = await MatchingApi.trialManagement.updateVolunteerContacted({
        trialVolunteerContactBody: { contactedAt },
        id: trialId,
        userId,
      });
      return response.data;
    },
    onMutate: async ({ trialId, contactedAt, userId, params, locationId }) => {
      await queryClient.cancelQueries({ queryKey: matchedVolunteersKeys.all() });

      const previousVolunteersList = queryClient.getQueryData<ListTrialMatchView>(
        matchedVolunteersKeys.list({ id: trialId, showDetails: false, ...params, locationId }),
      );

      if (previousVolunteersList) {
        const newVolunteersList = previousVolunteersList.items.map((user: TrialMatchView) =>
          user.userId === userId ? { ...user, contactedAt } : user,
        );
        queryClient.setQueryData(
          matchedVolunteersKeys.list({ id: trialId, showDetails: false, ...params, locationId }),
          {
            ...previousVolunteersList,
            items: newVolunteersList,
          },
        );
      }
      return { previousVolunteersList };
    },
    onError: (_, { trialId, params }, context) =>
      queryClient.setQueryData(
        matchedVolunteersKeys.list({ id: trialId, showDetails: false, ...params }),
        context?.previousVolunteersList,
      ),
  });
};

export const useVolunteerReachOut = (): UseMutationResult<
  TrialReachOutView,
  APIError,
  { communicationKey: TrialReachOutBodyCommunicationKeyEnum; trialId: string },
  unknown
> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trialId, communicationKey }) => {
      const response = await MatchingApi.trialManagement.volunteerReachOut({
        id: trialId,
        trialReachOutBody: { communicationKey },
      });
      return response.data;
    },
    onSuccess: (_, { trialId }) => {
      queryClient.invalidateQueries({ queryKey: volunteerOverviewKeys.list(trialId) });
      queryClient.invalidateQueries({ queryKey: trialsKeys.detail(trialId) });
      queryClient.invalidateQueries({ queryKey: funnelActivityKeys.all() });
    },
  });
};

// Mandates
export const useTeamMandates = (filters: TrialManagementApiGetTeamMandatesRequest) =>
  useQuery<TeamMandatesListView, APIError>({
    queryKey: teamMandatesKeys.list(filters),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getTeamMandates(filters);
      return response.data;
    },
    enabled: !!filters.teamId,
  });

export const useAssignMandate = (): UseMutationResult<
  void,
  APIError,
  {
    body: TrialManagementApiAssignMandateRequest;
    filters: TrialManagementApiGetTeamMandatesRequest;
  },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ body }) => {
      const response = await MatchingApi.trialManagement.assignMandate(body);
      return response.data;
    },
    onSuccess: (_, { filters }) => {
      queryClient.invalidateQueries({ queryKey: teamMandatesKeys.list(filters) });
    },
  });
};

export const useUnassignMandate = (): UseMutationResult<
  void,
  APIError,
  {
    body: TrialManagementApiUnassignMandateRequest;
    filters: TrialManagementApiGetTeamMandatesRequest;
  },
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ body }) => {
      const response = await MatchingApi.trialManagement.unassignMandate(body);
      return response.data;
    },
    onSuccess: (_, { filters }) => {
      queryClient.invalidateQueries({ queryKey: teamMandatesKeys.list(filters) });
    },
  });
};

export const useTrialAddEffect = (): UseMutationResult<
  void,
  APIError<TrialManagementApiAddEffectRequest>,
  TrialManagementApiAddEffectRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values) => {
      const res = await MatchingApi.trialManagement.addEffect(values);
      return res.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: trialsKeys.detail(id) });
    },
  });
};

export const useTrialDeleteEffect = (): UseMutationResult<
  void,
  APIError<TrialManagementApiRemoveEffectRequest>,
  TrialManagementApiRemoveEffectRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (values) => {
      const res = await MatchingApi.trialManagement.removeEffect(values);
      return res.data;
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: trialsKeys.detail(id) });
    },
  });
};

export const useResetTrialCache = (): UseMutationResult<
  void,
  APIError,
  { trialId: string },
  unknown
> => {
  return useMutation({
    mutationFn: async ({ trialId }) => {
      const response = await MatchingApi.trialManagement.resetTrialCache({ id: trialId });
      return response.data;
    },
  });
};

export const useRebuildUsersCache = (): UseMutationResult<
  void,
  APIError,
  { userIds: string[] },
  unknown
> => {
  return useMutation({
    mutationFn: async (usersCacheRegenerationBody) => {
      const response = await MatchingApi.trialManagement.recalculateMany({
        usersCacheRegenerationBody,
      });
      return response.data;
    },
  });
};

export const useAddCTTrial = (): UseMutationResult<
  void,
  APIError,
  { id: string; reprocessingConfirmed: boolean },
  unknown
> => {
  return useMutation({
    mutationFn: async ({ id, reprocessingConfirmed }) => {
      const response = await MatchingApi.trialManagement.addCT({
        id,
        trialAddCTBody: { reprocessingConfirmed },
      });
      return response.data;
    },
  });
};

export const useCreateMilestone = (): UseMutationResult<
  MilestoneView,
  APIError,
  TrialManagementApiCreateMilestoneRequest,
  unknown
> =>
  useMutation({
    mutationFn: async (body) => {
      const response = await MatchingApi.trialManagement.createMilestone(body);
      return response.data;
    },
  });

export const useTrialMilestones = (input: TrialManagementApiGetMilestonesRequest) => {
  return useQuery<ListMilestoneListView, APIError>({
    queryKey: trialMilestonesKeys.list(input),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getMilestones(input);
      return response.data;
    },
  });
};

export const useCreateMilestoneToken = (): UseMutationResult<
  MilestoneTokenView,
  APIError,
  TrialManagementApiGetMilestoneTokenRequest,
  unknown
> => {
  return useMutation({
    mutationFn: async (body) => {
      const response = await MatchingApi.trialManagement.getMilestoneToken(body);
      return response.data;
    },
  });
};

export const useSetCompanionInviteOnly = (): UseMutationResult<
  unknown,
  APIError,
  TrialManagementApiSetCompanionInviteOnlyRequest,
  unknown
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body) => {
      const response = await MatchingApi.trialManagement.setCompanionInviteOnly(body);
      return response.data;
    },
    onSuccess: (_, { id }) => queryClient.invalidateQueries({ queryKey: trialsKeys.detail(id) }),
  });
};

export const usePendingLocationChanges = (
  params: TrialManagementApiGetPendingLocationChangesRequest,
) =>
  useQuery<ListPendingLocationChangeView, APIError>({
    queryKey: pendingLocationChangesKeys.list(params),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getPendingLocationChanges(params);
      return response.data;
    },
  });

export const usePendingLocationChangesCount = (trialId: string, enabled = true) =>
  useQuery<number, APIError>({
    queryKey: pendingLocationChangesKeys.list({
      id: trialId,
      status: PendingLocationChangeStatus.Pending,
      limit: 1,
      offset: 0,
    }),
    queryFn: async () => {
      const response = await MatchingApi.trialManagement.getPendingLocationChanges({
        id: trialId,
        status: PendingLocationChangeStatus.Pending,
        limit: 1,
        offset: 0,
      });
      return response.data.count;
    },
    enabled: !!trialId && enabled,
  });
